import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { loginTestIds } from "../testids/authTestIds";

// Resolve through the Vite base so it also works under the /gcp/ edge mount,
// where the asset is served at /gcp/fastn-logo.jpg (BASE_URL ends with "/").
const FASTN_LOGO = `${import.meta.env.BASE_URL}fastn-logo.jpg`;

export function LoginPage(): React.ReactElement {
  const { authenticated, login } = useAuth();
  const [error, setError] = useState(false);

  // Redirect to profile if already authenticated
  if (authenticated) {
    return <Navigate to="/profile" replace />;
  }

  return <LoginRedirect onLogin={login} error={error} setError={setError} />;
}

/** Auto-redirect to Keycloak's branded login page */
function LoginRedirect({
  onLogin,
  error,
  setError,
}: {
  onLogin: () => void;
  error: boolean;
  setError: (v: boolean) => void;
}): React.ReactElement {
  useEffect(() => {
    // Small delay to avoid redirect loop if Keycloak check-sso is still resolving
    const timer = setTimeout(() => {
      try {
        onLogin();
      } catch {
        setError(true);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [onLogin, setError]);

  // Show minimal loading state while redirecting
  return (
    <div className="login-page" data-testid={loginTestIds.page}>
      <div className="login-container" data-testid={loginTestIds.container}>
        <img
          src={FASTN_LOGO}
          alt="fastn"
          className="login-logo"
          data-testid={loginTestIds.logo}
        />
        {error ? (
          <>
            <h1 className="login-title" data-testid={loginTestIds.errorTitle}>
              Unable to connect
            </h1>
            <p className="login-subtitle" data-testid={loginTestIds.errorSubtitle}>
              Could not reach the authentication server. Please try again.
            </p>
            <button
              onClick={() => { setError(false); try { onLogin(); } catch { setError(true); } }}
              className="btn btn--primary login-retry-btn"
              data-testid={loginTestIds.retryButton}
            >
              Retry
            </button>
          </>
        ) : (
          <>
            <h1 className="login-title" data-testid={loginTestIds.redirectingTitle}>
              Redirecting to sign in...
            </h1>
            <p className="login-subtitle" data-testid={loginTestIds.redirectingSubtitle}>
              Taking you to the login page
            </p>
            <div className="login-spinner-container" data-testid={loginTestIds.spinner}>
              <div className="login-spinner" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
